import { useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

// shadcn UI components
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../components/ui/use-toast';

// icons
import { User, ArrowLeft } from 'iconsax-react';

// project imports
import { forgotPassword } from '../../services/authApi';

// validation schema
const validationSchema = Yup.object({
  email: Yup.string().email('Invalid email').required('Email is required')
});

// ==============================|| FORGOT PASSWORD PAGE ||============================== //

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      // Store the email in sessionStorage
      sessionStorage.setItem('forgot_email', values.email);

      const result = await forgotPassword(values.email);
      if (result.success) {
        toast({
          title: "OTP Sent",
          description: "OTP has been sent to your email successfully!",
        });
        setTimeout(() => {
          navigate('/verify-otp');
        }, 1500);
      } else {
        sessionStorage.removeItem('forgot_email');
        toast({
          title: "Request Failed",
          description: result.message || 'Failed to request OTP',
          variant: "destructive"
        });
      }
    } catch (err) {
      sessionStorage.removeItem('forgot_email');
      console.error('Forgot password error:', err);
      toast({
        title: "Request Failed",
        description: err.response?.data?.message || 'Failed to request OTP. Please try again.',
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-background p-4 sm:p-6">
      <Card className="w-full max-w-[400px] border-border shadow-lg">
        <CardContent className="p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-3xl font-extrabold tracking-tight text-foreground">
              Forgot Password
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              Enter your email address to receive a 6-digit verification code.
            </p>
          </div>

          <Formik
            initialValues={{ email: '' }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
              <Form className="space-y-4">
                {/* Email Address */}
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground">
                      <User size={20} />
                    </div>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="admin@optihhcp.com"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`pl-10 ${
                        touched.email && errors.email ? 'border-destructive focus-visible:ring-destructive' : ''
                      }`}
                    />
                  </div>
                  {touched.email && errors.email && (
                    <p className="text-xs text-destructive">{errors.email}</p>
                  )}
                </div>

                {/* Submit button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-11 shadow-md transition-all duration-200 mt-2 flex items-center justify-center gap-2"
                >
                  {isSubmitting && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  {isSubmitting ? 'Requesting OTP...' : 'Request OTP'}
                </Button>

                {/* Back to Login */}
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  className="w-full text-muted-foreground hover:text-foreground text-sm font-medium flex items-center justify-center gap-1.5 h-10 mt-2"
                >
                  <ArrowLeft size={16} />
                  Back to Login
                </Button>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
